
import React, { useState, useMemo } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { BrandAwareButton as Button } from '@/components/brand/BrandAwareButton'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ChevronUp, ChevronDown, Search, Download, Filter, X, SlidersHorizontal } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { DataTableColumnConfig, ColumnConfig } from './DataTableColumnConfig'

interface Column {
  header: string
  key: string
  render?: (value: any, record: any) => React.ReactNode
  sortable?: boolean
}

interface EnhancedDataTableProps {
  columns: Column[]
  data: any[]
  onRecordClick?: (record: any) => void
  onExport?: (filters?: { searchTerm?: string; columnFilters?: Record<string, string> }) => void
  loading?: boolean
  searchPlaceholder?: string
  filterOptions?: { key: string; label: string; options: { value: string; label: string }[] }[]
  totalRecords?: number // Add this to show the actual total before filtering
}

export const EnhancedDataTable = ({ 
  columns, 
  data, 
  onRecordClick, 
  onExport, 
  loading, 
  searchPlaceholder = "Search records...",
  filterOptions = [],
  totalRecords
}: EnhancedDataTableProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState<Record<string, string>>({})
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({})
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false)
  // Column configuration state
  const [columnConfigs, setColumnConfigs] = useState<ColumnConfig[]>(() => 
    columns.map((col, index) => ({
      id: col.key,
      label: col.header,
      visible: true,
      order: index + 1
    }))
  )
  
  const [itemsPerPage, setItemsPerPage] = useState(50)

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(columnKey)
      setSortDirection('asc')
    }
  }

  const filteredAndSortedData = useMemo(() => {
    let filtered = data.filter(item => {
      // Search filter
      const matchesSearch = !searchTerm || 
        Object.values(item).some(value => 
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      
      // Basic filters (dropdown filters)
      const matchesFilters = Object.entries(filters).every(([key, value]) => {
        if (!value || value === 'all') return true
        return item[key]?.toString().toLowerCase() === value.toLowerCase()
      })
      
      // Column-specific filters
      const matchesColumnFilters = Object.entries(columnFilters).every(([key, value]) => {
        if (!value) return true
        const itemValue = item[key]?.toString().toLowerCase() || ''
        return itemValue.includes(value.toLowerCase())
      })
      
      return matchesSearch && matchesFilters && matchesColumnFilters
    })

    // Sort
    if (sortColumn) {
      filtered.sort((a, b) => {
        const aValue = a[sortColumn]
        const bValue = b[sortColumn]
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
        return 0
      })
    }

    return filtered
  }, [data, searchTerm, sortColumn, sortDirection, filters, columnFilters])

  const paginatedData = useMemo(() => {
    if (itemsPerPage >= filteredAndSortedData.length) {
      return filteredAndSortedData // Show all data if "All" is selected
    }
    const start = (currentPage - 1) * itemsPerPage
    return filteredAndSortedData.slice(start, start + itemsPerPage)
  }, [filteredAndSortedData, currentPage, itemsPerPage])

  const totalPages = itemsPerPage >= filteredAndSortedData.length ? 1 : Math.ceil(filteredAndSortedData.length / itemsPerPage)

  // Column management
  const handleColumnToggle = (columnId: string) => {
    setColumnConfigs(prev => 
      prev.map(col => 
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    )
  }

  const handleColumnReorder = (dragIndex: number, hoverIndex: number) => {
    const visibleColumns = columnConfigs.filter(col => col.visible).sort((a, b) => a.order - b.order)
    const draggedColumn = visibleColumns[dragIndex]
    const hoveredColumn = visibleColumns[hoverIndex]
    
    if (!draggedColumn || !hoveredColumn) return

    setColumnConfigs(prev => 
      prev.map(col => {
        if (col.id === draggedColumn.id) return { ...col, order: hoveredColumn.order }
        if (col.id === hoveredColumn.id) return { ...col, order: draggedColumn.order }
        return col
      })
    )
  }

  // Get visible columns in order
  const visibleColumns = useMemo(() => {
    const visibleConfigs = columnConfigs.filter(col => col.visible).sort((a, b) => a.order - b.order)
    return visibleConfigs.map(config => columns.find(col => col.key === config.id)).filter(Boolean) as Column[]
  }, [columnConfigs, columns])

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setCurrentPage(1)
  }

  const handleColumnFilterChange = (columnKey: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: value }))
    setCurrentPage(1)
  }

  const clearColumnFilter = (columnKey: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev }
      delete newFilters[columnKey]
      return newFilters
    })
  }

  const hasActiveColumnFilters = Object.keys(columnFilters).length > 0

  const getUniqueValues = (columnKey: string) => {
    const values = data.map(item => item[columnKey]).filter(Boolean)
    return [...new Set(values)].sort()
  }

  return (
    <div className="space-y-4">
      {/* Search, Filters, and Column Config */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <DataTableColumnConfig
          columns={columnConfigs}
          onColumnToggle={handleColumnToggle}
          onColumnReorder={handleColumnReorder}
        />
        
        <Dialog open={filtersDialogOpen} onOpenChange={setFiltersDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className={hasActiveColumnFilters ? 'border-primary text-primary' : ''}>
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              Filters
              {hasActiveColumnFilters && (
                <Badge variant="secondary" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                  {Object.keys(columnFilters).length}
                </Badge>
              )}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Column Filters</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Basic Filters */}
              {filterOptions.map(option => (
                <div key={option.key} className="space-y-2">
                  <label className="text-sm font-medium">{option.label}</label>
                  <Select value={filters[option.key] || 'all'} onValueChange={(value) => handleFilterChange(option.key, value)}>
                    <SelectTrigger>
                      <SelectValue placeholder={option.label} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All {option.label}</SelectItem>
                      {option.options.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              
              {/* Column Filters */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Column Filters</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {visibleColumns.map((column) => (
                    <div key={column.key} className="space-y-2">
                      <label className="text-sm font-medium">{column.header}</label>
                      <Input
                        placeholder={`Filter by ${column.header.toLowerCase()}...`}
                        value={columnFilters[column.key] || ''}
                        onChange={(e) => handleColumnFilterChange(column.key, e.target.value)}
                      />
                      {getUniqueValues(column.key).length > 0 && (
                        <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                          {getUniqueValues(column.key).slice(0, 8).map((value) => (
                            <Button
                              key={value}
                              variant="outline"
                              size="sm"
                              className="h-6 text-xs"
                              onClick={() => handleColumnFilterChange(column.key, value.toString())}
                            >
                              {value.toString()}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Clear All Filters */}
              {(Object.keys(columnFilters).length > 0 || Object.values(filters).some(v => v && v !== 'all')) && (
                <div className="border-t pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setColumnFilters({})
                      setFilters({})
                    }}
                    className="w-full"
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        
        {onExport && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onExport({ searchTerm, columnFilters })}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        )}
      </div>

      {/* Active Column Filters Display */}
      {hasActiveColumnFilters && (
        <div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-lg">
          <span className="text-sm font-medium text-muted-foreground">Active filters:</span>
          {Object.entries(columnFilters).map(([key, value]) => {
            const column = columns.find(col => col.key === key)
            return (
              <Badge key={key} variant="secondary" className="flex items-center gap-1">
                {column?.header}: {value}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                  onClick={() => clearColumnFilter(key)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            )
          })}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setColumnFilters({})}
            className="text-xs h-6"
          >
            Clear all
          </Button>
        </div>
      )}

      {/* Results Summary and Pagination Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-4">
          <span>
            Showing {paginatedData.length} of {filteredAndSortedData.length} filtered records
            {totalRecords && totalRecords !== filteredAndSortedData.length && (
              <span className="text-muted-foreground/70"> (from {totalRecords} total)</span>
            )}
          </span>
          
          <div className="flex items-center gap-2">
            <span className="text-xs">Show:</span>
            <Select value={itemsPerPage.toString()} onValueChange={(value) => {
              setItemsPerPage(Number(value))
              setCurrentPage(1)
            }}>
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="500">500</SelectItem>
                <SelectItem value="1000">1000</SelectItem>
                <SelectItem value={filteredAndSortedData.length.toString()}>All</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs">per page</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span>Page {currentPage} of {totalPages}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableHead key={column.key} className="font-medium">
                  {column.sortable !== false ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort(column.key)}
                      className="h-auto p-0 font-medium hover:bg-transparent"
                    >
                      {column.header}
                      {sortColumn === column.key && (
                        sortDirection === 'asc' ? 
                          <ChevronUp className="ml-1 h-4 w-4" /> : 
                          <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </Button>
                  ) : (
                    column.header
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="text-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="text-center py-8 text-muted-foreground">
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((record, index) => (
                <TableRow 
                  key={record.id || index}
                  className={onRecordClick ? "cursor-pointer hover:bg-muted/50" : ""}
                  onClick={() => onRecordClick?.(record)}
                >
                  {visibleColumns.map((column) => (
                    <TableCell key={column.key}>
                      {column.render 
                        ? column.render(record[column.key], record)
                        : record[column.key] || 'N/A'
                      }
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
